import { ARENA, MOTES } from '../../config/constants.js';
import { HealthPod } from '../entities/HealthPod.js';
import { XpMote } from '../entities/XpMote.js';

const SPAWN_INTERVAL = 15; // seconds between spawn attempts
const MAX_ACTIVE = 2;
const HEAL_PERCENT = 0.2; // normal pods restore 20% of max HP at pickup time
const EDGE_MARGIN = 20;
const MIN_DISTANCE_FROM_PLAYER = 120; // world px — keeps a pod from spawning as a freebie right on top of the player

/**
 * Owned once per run (not per wave/boss stage — see Game.js) — spawns a
 * capped number of one-time HealthPod pickups at random arena positions,
 * regardless of whether the player is currently in a wave or boss stage.
 *
 * Since BULLET-HEAVEN.md §H1 it also owns the XP mote field: the physical
 * drops that carry every minion's XP until the player walks over them. Motes
 * live here rather than in a system of their own for the reason pods do —
 * being run-owned rather than stage-owned means a mote dropped in the last
 * second of a wave stage survives the transition into the boss fight for
 * free, instead of needing a handoff nobody would remember to write.
 */
export class PickupSystem {
  constructor(eventBus) {
    this.eventBus = eventBus;
    this.pods = [];
    this.motes = [];
    this._spawnTimer = SPAWN_INTERVAL;
    // Moss pod-heal pets + Ancient's Regrowth legendary (data/pets.js) raise
    // this; the final heal is still capped by maxHp.
    this.healMultiplier = 1;
    this.ascensionHealMultiplier = 1;
    // Riptide (Rainbow Riptide pet): px/sec the pods are pulled toward the
    // player; 0 (default) leaves them stationary (body-contact pickup only).
    this.magnetSpeed = 0;
    // Act III's Tollkeeper (entities/Tollkeeper.js) eats motes inside its
    // radius and drops the lot when killed. Set by Game to a getter over the
    // live minion list; the default means this system keeps working — and keeps
    // costing nothing — in every fight and every test that has no Tollkeepers.
    this.getAbsorbers = () => [];
    // Extra magnet radius from the live graze chain (BULLET-HELL.md §D5) —
    // read per frame rather than set, same hook shape as getAbsorbers, so a
    // run/test without a chain pays nothing and needs to know nothing.
    this.getMagnetBonus = () => 0;

    // Motes are dropped by whoever knows what they're worth — ProgressionSystem
    // owns the XP formula and emits `mote:dropped`; this system owns where they
    // are and when they're picked up. Neither has to know the other's job.
    this._onMoteDropped = ({ x, y, xp, tier }) => this.dropMote({ x, y, xp, tier });
    eventBus.on('mote:dropped', this._onMoteDropped);
  }

  /**
   * Total XP sitting uncollected on the floor. Exists so the conservation
   * property — every point a minion banks is eventually paid, none created,
   * none lost — is directly assertable rather than inferred from the XP bar.
   */
  get moteXpOnField() {
    let total = 0;
    for (const mote of this.motes) if (mote.active) total += mote.xp;
    return total;
  }

  dropMote({ x, y, xp, tier = 'small' }) {
    if (xp <= 0) return null;
    const mote = new XpMote({ x, y, xp, tier });
    this.motes.push(mote);
    // Over the cap, the OLDEST motes vacuum rather than the newest being
    // dropped on the floor: capping by refusing to spawn would silently eat
    // XP, and capping by deleting would eat it louder. Vacuuming pays it out.
    if (this.motes.length > MOTES.maxActive) {
      const excess = this.motes.length - MOTES.maxActive;
      for (let i = 0; i < excess; i++) this.motes[i].vacuuming = true;
    }
    return mote;
  }

  /**
   * Every mote on the field streams to the player at once. Called on a stage
   * clear (Game.js) so nothing is stranded by a transition, and it is also the
   * genre's signature image on purpose — a stage ending in a torrent of motes
   * and back-to-back level-ups is the payoff the whole microloop is for.
   */
  vacuumAll() {
    for (const mote of this.motes) mote.vacuuming = true;
  }

  update(dt, player) {
    if (this.disabled) return;
    this._updateMotes(dt, player);
    this._spawnTimer -= dt;
    if (this._spawnTimer <= 0) {
      this._spawnTimer = SPAWN_INTERVAL;
      // Hunger pact: no NEW pods, but any already on the floor stay
      // collectible — the drawback is the future, not a rug-pull.
      if (this.pods.length < MAX_ACTIVE && !player.podSpawnDisabled) this._trySpawn(player);
    }

    for (const pod of this.pods) {
      if (!pod.active) continue;
      // Riptide (Rainbow Riptide pet): draw pods toward the player from
      // anywhere. Capped at the remaining distance so it's a steady pull, not
      // a teleport, and never overshoots.
      if (this.magnetSpeed > 0) {
        const mdx = player.x - pod.x;
        const mdy = player.y - pod.y;
        const md = Math.hypot(mdx, mdy) || 1;
        const step = Math.min(md, this.magnetSpeed * dt);
        pod.x += (mdx / md) * step;
        pod.y += (mdy / md) * step;
      }
      const dist = Math.hypot(pod.x - player.x, pod.y - player.y);
      if (dist < pod.radius + player.radius) {
        const hpBefore = player.hp;
        // Field Medic (level-up card) rides on the same footing as the pet
        // pod-healing stat rather than multiplying with it.
        const cardBonus = 1 + (player.podHealBonus ?? 0);
        player.hp = Math.min(player.maxHp,
          player.hp + player.maxHp * HEAL_PERCENT * this.healMultiplier * cardBonus * this.ascensionHealMultiplier);
        pod.active = false;
        this.eventBus.emit('pod:collected', { x: pod.x, y: pod.y });
        // Fires alongside pod:collected (which stays pod-position-anchored for
        // the pickup burst) so Game.js can give this the same visible "you
        // just healed" treatment as Vampiric — see Player.js's applyLifesteal.
        this.eventBus.emit('player:healed', { amount: player.hp - hpBefore, source: 'pod' });
      }
    }
    this.pods = this.pods.filter((p) => p.active);
  }

  /**
   * Magnet, vacuum, collect. The magnet radius is a real stat
   * (`player.magnetRadius`, additive on top of MOTES.magnetBase) rather than a
   * constant — it's a new, permanently valuable axis for passives, pets and
   * meta upgrades, which the game did not previously have anywhere to put.
   */
  _updateMotes(dt, player) {
    if (this.motes.length === 0) return;
    // The graze-chain term is deliberately additive with the stat: a build
    // that invested in magnet radius still gets the full chain bonus on top,
    // because the chain is paying for what you did this second, not for what
    // you picked at level 4.
    const magnetRadius = MOTES.magnetBase + (player.magnetRadius ?? 0) + (this.getMagnetBonus() || 0);
    let collectedXp = 0;
    let collectedCount = 0;
    let lastX = 0;
    let lastY = 0;

    const absorbers = this.getAbsorbers();
    for (const mote of this.motes) {
      if (!mote.active) continue;
      mote.age += dt;
      // A Tollkeeper standing over a mote takes it before the player can. The
      // XP isn't destroyed — it's banked on the creature and paid out in full
      // when it dies, so ignoring one costs the run's economy, not its XP.
      if (absorbers.length && this._absorb(mote, absorbers)) continue;
      const dx = player.x - mote.x;
      const dy = player.y - mote.y;
      const dist = Math.hypot(dx, dy) || 1;

      // Past its lifetime a mote stops waiting to be walked over. It is never
      // deleted — a mote that expired to nothing would mean a cautious player
      // permanently loses progression for playing carefully, which is the
      // opposite of what a reward loop should teach.
      if (!mote.vacuuming && mote.age >= MOTES.lifetime) mote.vacuuming = true;

      const speed = mote.vacuuming ? MOTES.vacuumSpeed : (dist <= magnetRadius ? MOTES.magnetSpeed : 0);
      if (speed > 0) {
        const stepDist = Math.min(dist, speed * dt);
        mote.x += (dx / dist) * stepDist;
        mote.y += (dy / dist) * stepDist;
      }

      if (Math.hypot(player.x - mote.x, player.y - mote.y) < mote.radius + player.radius) {
        mote.active = false;
        collectedXp += mote.xp;
        collectedCount++;
        lastX = mote.x;
        lastY = mote.y;
      }
    }

    if (collectedCount > 0) {
      // One event for everything collected this tick, not one per mote. Late
      // in a run this fires with dozens of motes in a single frame, and a
      // per-mote event would mean dozens of synchronous XP grants (each able
      // to open a level-up pick screen) and dozens of sfx triggers inside one
      // frame. The count rides along so feedback can scale with it.
      this.eventBus.emit('mote:collected', { x: lastX, y: lastY, xp: collectedXp, count: collectedCount });
    }
    this.motes = this.motes.filter((m) => m.active);
  }

  /** Hands `mote` to the first absorber covering it; true if it was taken. */
  _absorb(mote, absorbers) {
    for (const absorber of absorbers) {
      if (absorber.isDead) continue;
      if (Math.hypot(absorber.x - mote.x, absorber.y - mote.y) > absorber.absorbRadius) continue;
      absorber.absorb(mote.xp);
      mote.active = false;
      return true;
    }
    return false;
  }

  forEachMote(fn) {
    for (const mote of this.motes) if (mote.active) fn(mote);
  }

  /** Called when a run's Game is torn down — the bus outlives this system. */
  destroy() {
    this.eventBus.off?.('mote:dropped', this._onMoteDropped);
  }

  _trySpawn(player) {
    // A few tries to land somewhere not right on top of the player, rather
    // than exact rejection sampling — good enough for a rare pickup.
    for (let attempt = 0; attempt < 8; attempt++) {
      const x = ARENA.x + EDGE_MARGIN + Math.random() * (ARENA.width - EDGE_MARGIN * 2);
      const y = ARENA.y + EDGE_MARGIN + Math.random() * (ARENA.height - EDGE_MARGIN * 2);
      if (Math.hypot(x - player.x, y - player.y) >= MIN_DISTANCE_FROM_PLAYER) {
        this.pods.push(new HealthPod({ x, y }));
        // Pods previously appeared in total silence — easy to never notice
        // one spawned at all. Feedback (shimmer + soft chime) rides this.
        this.eventBus.emit('pod:spawned', { x, y });
        return;
      }
    }
  }

  forEachActive(fn) {
    for (const pod of this.pods) if (pod.active) fn(pod);
  }
}
